/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { AmbiguousOfficeInfo, EcsperErrorMeta } from '@ecsper/types';

/**
 * Handles the `AMBIGUOUS_OFFICE` error from the SSO exchange.
 *
 * When the Platform returns `AMBIGUOUS_OFFICE` (409), the error meta contains
 * a list of offices. This module stores the list and provides the selection
 * mechanism. After user selection, the auth service re-calls the exchange
 * with `office_id_hint` set.
 *
 * Source: CTR-PL-MS-OFFICESSO-001 v1.0.0 §6.1 (`AMBIGUOUS_OFFICE` error;
 *         Q-01, Q-05 resolved: optional hint, return list, re-call)
 */
export class OfficePicker {
  private options: AmbiguousOfficeInfo[] | null = null;
  private selectedOfficeId: string | null = null;

  /** Store the office options from an `AMBIGUOUS_OFFICE` error */
  public setOptions(offices: AmbiguousOfficeInfo[]): void {
    this.options = [...offices];
  }

  /** Get the current options (null if not in picker state) */
  public getOptions(): AmbiguousOfficeInfo[] | null {
    return this.options === null ? null : [...this.options];
  }

  /** Record the user's selection */
  public selectOffice(officeId: string): void {
    this.selectedOfficeId = officeId;
  }

  /** Get the selected `office_id_hint` for the re-call */
  public getSelectedOfficeId(): string | null {
    return this.selectedOfficeId;
  }

  /** Clear picker state */
  public clear(): void {
    this.options = null;
    this.selectedOfficeId = null;
  }
}

/**
 * Parse `meta.offices` from an `AMBIGUOUS_OFFICE` error envelope.
 *
 * @returns A validated office list, or `null` when meta is missing or malformed.
 */
export function parseAmbiguousOfficesFromMeta(
  meta: EcsperErrorMeta | undefined,
): AmbiguousOfficeInfo[] | null {
  if (meta === undefined) return null;
  const officesUnknown = meta['offices'];
  if (!Array.isArray(officesUnknown)) return null;
  const offices: AmbiguousOfficeInfo[] = [];
  for (const item of officesUnknown) {
    if (typeof item !== 'object' || item === null) return null;
    const record = item as { office_id?: unknown; office_name?: unknown };
    if (typeof record.office_id !== 'string' || typeof record.office_name !== 'string') {
      return null;
    }
    offices.push({ office_id: record.office_id, office_name: record.office_name });
  }
  return offices.length === 0 ? null : offices;
}
